import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {SequenceAnalysisSummary} from 'biojs/Sequence';
import {Sequence} from 'components/Sequence';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector from 'file-browser/FolderSelector';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {getInitialFolderIdForDuplicate} from 'molbio/components/getInitialFolderIdForDuplicate';
import * as SequenceDAO from 'molbio/dao/SequenceDAO';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

export function runDuplicatePolymerModal(
  polymer: AaSequence | Sequence,
  tagSchemaId: string | null
): Promise<string | null> {
  const initialFolderId = getInitialFolderIdForDuplicate(polymer);

  if (polymer instanceof AaSequence) {
    return runModal(DuplicatePolymerModal, {
      folderItemType: ModelTypeConstants.PROTEIN,
      initialName: `${polymer.name()} copy`,
      initialFolderId,
      initialTagSchemaId: tagSchemaId,
      isSchemaEditable: false,
      polymerId: polymer.id,
      polymerVersionId: polymer.getActiveVersionId(),
      sequenceAnalysisIds: null,
    });
  } else {
    return runModal(DuplicatePolymerModal, {
      folderItemType: polymer.subtype(),
      initialName: `${polymer.name()} copy`,
      initialFolderId,
      initialTagSchemaId: tagSchemaId,
      isSchemaEditable: false,
      polymerId: polymer.get('id'),
      polymerVersionId: polymer.get('activeVersionId'),
      sequenceAnalysisIds: polymer
        .get('_sequence')
        .get('analyses')
        .map((analysis: SequenceAnalysisSummary) => analysis.id),
    });
  }
}

export interface DuplicatePolymerModalProps {
  folderItemType: OligoAwareFolderItemType;
  initialFolderId: string | null;
  initialName: string;
  initialTagSchemaId: string | null;
  isSchemaEditable: boolean;
  polymerId: string;
  polymerVersionId: number;
  sequenceAnalysisIds: Array<number> | null;
}

function DuplicatePolymerModal({
  folderItemType,
  initialName,
  initialFolderId,
  initialTagSchemaId,
  isSchemaEditable,
  onRequestClose,
  onRequestSubmit,
  polymerId,
  polymerVersionId,
  sequenceAnalysisIds,
}: DuplicatePolymerModalProps & {
  onRequestClose: () => void;
  onRequestSubmit: (createdPolymerId: string) => void;
}): JSX.Element {
  const [name, setName] = useState<string>(initialName);
  const [tagSchemaId, setTagSchemaId] = useState<string | null>(initialTagSchemaId);
  const [folderId, setFolderId] = useState<string | null>(initialFolderId);

  async function onSubmitForNucleotideSequence(folderId: string): Promise<string> {
    const cloneResponse = await withLoadingScreen(
      SequenceDAO.cloneVersion(polymerId, polymerVersionId, folderId, name, sequenceAnalysisIds, tagSchemaId),
      'Creating a copy...'
    );
    return cloneResponse.id;
  }

  function onSubmitForProtein(folderId: string): Promise<string | null> {
    // TODO(BNCH-123297): Call endpoint to clone protein version
    return Promise.resolve(null);
  }

  return (
    <Modal
      title="Create a copy"
      onRequestClose={onRequestClose}
      onRequestSubmit={async () => {
        assert(folderId != null, 'Unexpectedly received null folderId');
        let clonedId;
        if (APIUtil.isNucleotideSequence(polymerId)) {
          clonedId = await onSubmitForNucleotideSequence(folderId);
        } else if (APIUtil.isProtein(polymerId)) {
          clonedId = await onSubmitForProtein(folderId);
        } else {
          assert(false, 'Unexpectedly received non-sequence ID in DuplicatePolymerModal');
        }
        if (clonedId != null) {
          FolderDAOCache.invalidateFolderContents(folderId);
          FileBrowserActions.openFile(clonedId);
          onRequestSubmit(clonedId);
        } else {
          onRequestClose();
        }
      }}
      buttons={[{text: 'Create', submit: true, disabled: folderId == null}]}
    >
      <Row className={css(styles.topRow)}>
        <div className={css(styles.rowItem)}>
          <FieldLabel label="Name" isRequired={true} />
          <TextInput
            dataTestAttrs={{
              component: 'DuplicatePolymerModal',
              element: 'nameInput',
            }}
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setName(e.target.value);
            }}
          />
        </div>
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_SET_SCHEMA}) && (
          <div className={css(styles.rowItem)}>
            <FieldLabel label="Schema" isRequired={true} />
            <TagSchemaSelector
              allowNone={true}
              isDisabled={!isSchemaEditable}
              oligoAwareFolderItemTypes={[folderItemType]}
              onChange={(tagSchema) => {
                setTagSchemaId(tagSchema ? tagSchema.id : null);
              }}
              selectedTagSchemaId={tagSchemaId}
            />
          </div>
        )}
      </Row>
      <Row className={css(styles.bottomRow)}>
        <FieldLabel label="Folder" isRequired={true} />
        <FolderSelector
          allowNone={false}
          value={folderId}
          onChange={(e) => {
            setFolderId(e.target.value);
          }}
        />
      </Row>
    </Modal>
  );
}

export default DuplicatePolymerModal;

const styles = StyleSheet.create({
  topRow: {display: 'flex', margin: '0 0 16px 0', columnGap: '8px'},
  bottomRow: {margin: '8px'},
  rowItem: {flex: 1},
});
